import { useGetAdminDropoffLocations } from "@/api/admin-carrier-dropoff-locations";
import {
  useListCarrierAddOnServiceAccess,
  useListCarrierAddOnServices,
} from "@/api/carrier-add-on-services";
import type { DispatchCenterDropoffCell } from "@/api/dispatch-center-dropoffs";
import { Button } from "@/components/ui/button";

/** Server-owned field diagnostics remain separate from route availability.
 * Older responses with raw fields cannot establish field validity. */
export function PickupHandoffDiagnostics({
  providerCarrierId,
  cell,
  refreshingMapping,
}: {
  providerCarrierId: string;
  cell: DispatchCenterDropoffCell;
  refreshingMapping: boolean;
}) {
  const locations = useGetAdminDropoffLocations(cell.labelCarrier.id, {
    includeInactive: true,
  });
  const services = useListCarrierAddOnServices({
    providerCarrierId,
    type: "PICKUP",
    includeInactive: true,
  });
  const access = useListCarrierAddOnServiceAccess({
    labelCarrierId: cell.labelCarrier.id,
  });
  const queries = [locations, services, access];
  const failed = queries.some((query) => query.isError);
  const pending = queries.some((query) => query.isPending);
  const refreshing =
    refreshingMapping || queries.some((query) => query.isFetching);
  const location = locations.data?.find(
    (row) => row.id === cell.defaultDropoffLocationId
  );

  return (
    <div className="space-y-2 rounded-md border p-3 text-xs">
      <p>
        <strong>API connection / supply:</strong> Review supply and connection
        status in{" "}
        <a className="underline" href="/carriers/list">
          Admin Carrier Configuration
        </a>
        . Successful quotes or labels do not confirm pickup handoff setup.
      </p>
      <p>
        <strong>Platform pickup grant:</strong> Granted to this center's
        provider for {cell.labelCarrier.name}.{" "}
        <a className="underline" href="/add-on-services/grants">
          Review Platform Grants
        </a>
      </p>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={refreshing}
        onClick={() =>
          void Promise.all(queries.map((query) => query.refetch()))
        }
      >
        Refresh handoff details
      </Button>
      {failed ? (
        <p role="alert">
          Could not read handoff details. Service enablement and receiving-depot
          configuration are unconfirmed. Retry with Refresh handoff details.
        </p>
      ) : pending || refreshing ? (
        <p role="status">
          Reading handoff details… Configuration is unconfirmed.
        </p>
      ) : !locations.data || !services.data || !access.data ? (
        <p role="alert">
          Handoff details are missing. Configuration is unconfirmed.
        </p>
      ) : (
        <>
          <div className="space-y-1">
            <p className="font-medium">Individual pickup services</p>
            {services.data.length === 0 && (
              <p>No pickup services configured for this provider.</p>
            )}
            {services.data.map((service) => (
              <p key={service.id}>
                {service.name}:{" "}
                {!service.active
                  ? "Inactive service"
                  : access.data.some(
                        (row) =>
                          row.carrierAddOnServiceId === service.id &&
                          row.enabled
                      )
                    ? "Enabled"
                    : "Not enabled"}
                .{" "}
                <a
                  className="underline"
                  href={`/add-on-services/services/${encodeURIComponent(service.id)}`}
                >
                  Review service → Enablement
                </a>
              </p>
            ))}
          </div>
          <div className="space-y-1">
            <p className="font-medium">Mapped receiving depot</p>
            {!cell.defaultDropoffLocationId ? (
              <p>
                No receiving location mapped. Select an active location above.
              </p>
            ) : !location ? (
              <p>
                The mapped receiving location is missing or unavailable. Review
                locations in Admin and select an active mapping.
              </p>
            ) : (
              <>
                <p>
                  {location.name}:{" "}
                  {location.active
                    ? "Active location"
                    : "Inactive location — select an active mapping"}
                  .
                </p>
                <p>
                  Physical address:{" "}
                  {location.handoffDiagnostics?.physicalAddress === "missing"
                    ? "Missing"
                    : location.address
                      ? [
                          location.address.line1,
                          location.address.city,
                          location.address.province,
                        ]
                          .filter(Boolean)
                          .join(", ") || "Missing"
                      : "Missing"}
                  .
                </p>
                <p>
                  Physical point:{" "}
                  {location.handoffDiagnostics?.physicalPoint === "valid"
                    ? "Valid coordinates"
                    : location.handoffDiagnostics?.physicalPoint === "invalid"
                      ? "Invalid — verify the facility coordinates"
                      : location.address?.location == null ||
                          location.handoffDiagnostics?.physicalPoint ===
                            "missing"
                        ? "Missing"
                        : "Recorded; verification unconfirmed"}
                  .
                </p>
                <p>
                  Verified timezone:{" "}
                  {location.handoffDiagnostics?.timezone === "valid"
                    ? `${location.address?.timezone ?? "Timezone"} — valid`
                    : location.handoffDiagnostics?.timezone === "invalid"
                      ? "Invalid — verify the facility timezone"
                      : !location.address?.timezone ||
                          location.handoffDiagnostics?.timezone === "missing"
                        ? "Missing — verify the facility timezone"
                        : `${location.address.timezone} recorded; verification unconfirmed`}
                  .
                </p>
                <p>
                  Operating hours:{" "}
                  {location.handoffDiagnostics?.operatingHours === "valid"
                    ? "Weekly schedule format accepted"
                    : location.handoffDiagnostics?.operatingHours === "invalid"
                      ? "Invalid — correct the receiving hours"
                      : location.handoffDiagnostics?.operatingHours ===
                          "closed_all_week"
                        ? "Closed all week — no receiving windows"
                        : location.operatingHours == null ||
                            location.handoffDiagnostics?.operatingHours ===
                              "missing"
                          ? "Missing"
                          : "Recorded; validity and open days unconfirmed"}
                  .
                </p>
              </>
            )}
            <p>
              Manage locations in{" "}
              <a className="underline" href="/carriers/list">
                Admin Carriers → {cell.labelCarrier.name} → Edit Configuration →
                Dropoff Locations
              </a>
              .
            </p>
            <p className="text-muted-foreground">
              Edit the receiving hours and verified timezone in the location's
              Edit dialog. After saving, refresh handoff details. Field validity
              does not guarantee pickup availability; the Server still checks
              the complete sender, package, date and route.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
